import type { z } from "zod";

const error = (): z.core.$ZodErrorMap => {
  const Sizable = {
    string: { unit: "teken", pluralUnit: "tekens" },
    file: { unit: "byte", pluralUnit: "bytes" },
    array: { unit: "element", pluralUnit: "elementen" },
    set: { unit: "item", pluralUnit: "items" },
  };

  function getSizing(origin: string) {
    return Sizable[origin as keyof typeof Sizable] ?? null;
  }

  const parsedType = (data: unknown): string => {
    const t = typeof data;
    switch (t) {
      case "number": {
        return Number.isNaN(data) ? "NaN" : "getal";
      }
      case "object": {
        if (Array.isArray(data)) {
          return "array";
        }
        if (data === null) {
          return "null";
        }
        if (Object.getPrototypeOf(data) !== Object.prototype && (data as { constructor?: { name: string } }).constructor) {
          return (data as { constructor: { name: string } }).constructor.name;
        }
      }
    }
    return t;
  };

  const Nouns: Record<string, string> = {
    regex: "invoer",
    email: "e-mailadres",
    url: "URL",
    emoji: "emoji",
    uuid: "UUID",
    uuidv4: "UUIDv4",
    uuidv6: "UUIDv6",
    nanoid: "nanoid",
    guid: "GUID",
    cuid: "cuid",
    cuid2: "cuid2",
    ulid: "ULID",
    xid: "XID",
    ksuid: "KSUID",
    datetime: "datetime",
    date: "datum",
    time: "tijd",
    duration: "duur",
    ipv4: "IPv4-adres",
    ipv6: "IPv6-adres",
    cidrv4: "IPv4-bereik",
    cidrv6: "IPv6-bereik",
    base64: "base64 string",
    base64url: "base64url string",
    json_string: "JSON string",
    e164: "E.164-nummer",
    jwt: "JWT",
    template_literal: "invoer",
  };

  return (issue) => {
    switch (issue.code) {
      case "invalid_type":
        // Handle required fields (undefined, null, or empty string)
        if (issue.input === undefined || issue.input === null || issue.input === "") {
          return "Verplicht";
        }

        // Handle type mismatches with actual values
        if (issue.expected === "number") {
          return "Een nummer verwacht";
        }
        if (issue.expected === "boolean") {
          return "Een boolean verwacht";
        }
        if (issue.expected === "date") {
          return "Een datum verwacht";
        }

        return `${issue.expected} verwacht, ${parsedType(issue.input)} ontvangen`;

      case "invalid_value": {
        const values = issue.values.map((v) => String(v));
        if (values.length === 1) {
          return `Ongeldige invoer: ${values[0]} verwacht`;
        }
        return `Ongeldige waarde. Verwacht ${values.join(" | ")}`;
      }

      case "too_big": {
        const sizing = getSizing(issue.origin);
        if (sizing) {
          const unit = Number(issue.maximum) === 1 ? sizing.unit : sizing.pluralUnit;
          if (issue.exact) {
            return `Moet precies ${issue.maximum} ${unit} bevatten`;
          }
          return `Moet maximaal ${issue.maximum} ${unit} bevatten`;
        }
        if (issue.inclusive) {
          return `Nummer moet kleiner dan of gelijk zijn aan ${issue.maximum}`;
        }
        return `Nummer moet kleiner zijn dan ${issue.maximum}`;
      }

      case "too_small": {
        const sizing = getSizing(issue.origin);
        if (sizing) {
          const unit = Number(issue.minimum) === 1 ? sizing.unit : sizing.pluralUnit;
          if (issue.exact) {
            return `Moet precies ${issue.minimum} ${unit} bevatten`;
          }
          return `Moet minimaal ${issue.minimum} ${unit} bevatten`;
        }
        if (issue.inclusive) {
          return `Nummer moet groter dan of gelijk zijn aan ${issue.minimum}`;
        }
        return `Nummer moet groter zijn dan ${issue.minimum}`;
      }

      case "invalid_format": {
        const _issue = issue as typeof issue & { format?: string; prefix?: string; suffix?: string; includes?: string };
        if (_issue.format === "starts_with") {
          return `Moet beginnen met "${_issue.prefix}"`;
        }
        if (_issue.format === "ends_with") {
          return `Moet eindigen met "${_issue.suffix}"`;
        }
        if (_issue.format === "includes") {
          return `Moet "${_issue.includes}" bevatten`;
        }
        if (_issue.format === "regex") {
          return "Ongeldig formaat";
        }
        if (_issue.format === "email") {
          return "Ongeldig e-mailadres";
        }
        if (_issue.format === "url") {
          return "Ongeldige URL";
        }
        return `Ongeldige ${Nouns[_issue.format] ?? _issue.format}`;
      }

      case "not_multiple_of":
        return `Nummer moet een veelvoud zijn van ${issue.divisor}`;

      case "unrecognized_keys":
        return `Onbekende sleutel${issue.keys.length > 1 ? "s" : ""}: ${issue.keys.join(", ")}`;

      case "invalid_key":
        return `Ongeldige sleutel in ${issue.origin}`;

      case "invalid_union":
        return "Ongeldige invoer";

      case "invalid_element":
        return `Ongeldige waarde in ${issue.origin}`;

      default:
        return "Ongeldige invoer";
    }
  };
};

export default function () {
  return {
    localeError: error(),
  };
}
